import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { Check, Download, Loader2, Maximize2, Minimize2, Presentation } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { CoursePlayerShell } from '../../components/learning/CoursePlayerShell';
import AccessRestricted from '../../components/AccessRestricted';
import { LoadingPageSkeleton } from '../../components/common/LoadingPageSkeleton';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { useAuth } from '../../context/AuthContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { learningApi } from '../../utils/learning-api';
import { AssignmentStatus, Course, CourseCertificate, ScormSession } from '../../types/learning.types';
import { courseContentTypeLabel } from '../../utils/learning';
import { API_BASE } from '../../utils/api';
import { generateLearningCertificatePdf } from '../../utils/learning-certificate-pdf';
import { cn } from '../../components/ui/utils';

/**
 * The slice of a course the player actually renders. A learner's course comes from the
 * library (a full `Course`), an onboarding course from the portal payload (a smaller
 * projection), and this is the part they agree on.
 */
type PlayerCourse = Pick<
    Course,
    'id' | 'title' | 'contentType' | 'contentFileUrl' | 'issuesCertificate'
> & {
    category: { name: string; colour: string } | null;
};

/**
 * The training record behind the course. `courseTitle` / `category` are a display fallback:
 * the learner library can drop a course while its training record stays (archived content).
 */
interface PlayerRecord {
    status: AssignmentStatus;
    scormBookmark: boolean;
    courseTitle?: string;
    category?: { name: string; colour: string } | null;
}

/**
 * Where the player's state comes from. A learner's own course lives in their training
 * register and mints certificates; an onboarding course lives on the hire's onboarding
 * request, mandatory and certificate-free until HR adds it to their profile. Everything the
 * two do differently is these six calls — the shell and the bodies are shared.
 */
interface CoursePlayerSource {
    load(): Promise<{ course: PlayerCourse | null; record: PlayerRecord; certificate: CourseCertificate | null }>;
    refresh(): Promise<{ record: PlayerRecord; certificate: CourseCertificate | null }>;
    /**
     * Marks the course In Progress on open. Only onboarding implements it: a mandatory
     * pre-start course has no intro page to press Start on, while the learner's own course is
     * started from its intro page — and its player can also be opened straight from a training
     * record whose course has left the library, where there is no live enrolment to move.
     */
    start?(): Promise<void>;
    session(startFresh: boolean): Promise<ScormSession>;
    complete(): Promise<void>;
}

/** The learner's own course: /api/learning/my + library + certificates. */
const createLearnerSource = (courseId: number): CoursePlayerSource => {
    /** The learner's training record for this course, plus the certificate it has earned. */
    const readRecord = async (): Promise<{ record: PlayerRecord; certificate: CourseCertificate | null }> => {
        const [my, certs] = await Promise.all([
            learningApi.learner.my(),
            learningApi.learner.certificates(),
        ]);
        const row = [...my.assigned, ...my.self].find((r) => r.courseId === courseId) ?? null;
        return {
            record: row
                ? {
                    status: row.status,
                    scormBookmark: row.scormBookmark,
                    courseTitle: row.courseTitle,
                    category: row.category,
                }
                : { status: 'Assigned', scormBookmark: false },
            certificate: row ? certs.find((c) => c.courseAssignmentId === row.id) ?? null : null,
        };
    };

    return {
        load: async () => {
            const [state, library] = await Promise.all([readRecord(), learningApi.learner.library()]);
            return { ...state, course: library.find((c) => c.id === courseId) ?? null };
        },
        refresh: readRecord,
        session: (startFresh) => learningApi.learner.scormSession(courseId, startFresh),
        complete: async () => {
            await learningApi.learner.complete(courseId);
        },
    };
};

/**
 * A mandatory onboarding course: the hire's onboarding request, not a course assignment.
 * There is no certificate to read — HR issues one when the course is added to the hire's
 * learner profile after conversion.
 */
const createOnboardingSource = (courseId: number): CoursePlayerSource => {
    const read = async () => {
        const { course, record } = await learningApi.onboarding.course(courseId);
        return {
            course: {
                id: course.id,
                title: course.title,
                contentType: course.contentType,
                contentFileUrl: course.contentFileUrl,
                issuesCertificate: course.issuesCertificate,
                category: course.category,
            },
            record: { status: record.status, scormBookmark: record.scormBookmark },
            certificate: null,
        };
    };

    return {
        load: read,
        refresh: async () => {
            const { record, certificate } = await read();
            return { record, certificate };
        },
        start: async () => {
            await learningApi.onboarding.progress(courseId, 'start');
        },
        session: (startFresh) => learningApi.onboarding.session(courseId, startFresh),
        complete: async () => {
            await learningApi.onboarding.progress(courseId, 'complete');
        },
    };
};

/**
 * Course player (LEARNING.md §7). One shell, a body per content type.
 *
 * PDF renders inline; PPTX is download + honour-based completion; SCORM is a package that
 * reports its own progress. The first two are documents, the third is a program, and that
 * difference is why SCORM needs a session (a scoped token) and a runtime injected into the
 * page the API serves — see §13.1. When such a package reports enough to pass, the course
 * completes on its own and this page refreshes itself to show the certificate.
 *
 * A SCORM course is NEVER completed from here: the package is what records progress, and an
 * always-available "mark complete" let a learner finish a course before starting it. PDF and
 * PPTX have no runtime to report anything, so their honour-based button stays.
 *
 * The same player serves the onboarding portal (`onboarding`): a hire's mandatory pre-start
 * courses are reached through the onboarding request rather than the training register, and
 * differ only in where their state lives — hence the source abstraction above.
 */
export default function CoursePlayerPage({ onboarding = false }: { onboarding?: boolean }) {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { courseId: courseIdParam } = useParams<{ courseId: string }>();

    const canRead = ability.can(ACTIONS.READ, RESOURCES.COURSE_ENROLLMENT);
    // Onboarding courses are self-service: the portal endpoints authorise by request
    // ownership, so the learner-surface CASL gate must not stand in front of them.
    const allowed = onboarding || canRead;
    const courseId = Number(courseIdParam);

    const source = useMemo<CoursePlayerSource>(
        () => (onboarding ? createOnboardingSource(courseId) : createLearnerSource(courseId)),
        [onboarding, courseId],
    );

    const [course, setCourse] = useState<PlayerCourse | null>(null);
    const [record, setRecord] = useState<PlayerRecord | null>(null);
    const [certificate, setCertificate] = useState<CourseCertificate | null>(null);
    const [completed, setCompleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    // SCORM only: the play-through session, null until minted (or when the type isn't SCORM).
    const [scormSession, setScormSession] = useState<ScormSession | null>(null);
    const [scormError, setScormError] = useState<string | null>(null);
    /**
     * SCORM only: the learner's answer to "resume or start again?", captured once per visit.
     * `null` while it is unresolved — either the record has not loaded, or the dialog is up.
     * Settling it here (rather than reading the record each render) keeps a later record
     * refresh from re-minting the session and reloading the package mid-course.
     */
    const [resume, setResume] = useState<boolean | null>(null);

    /** The SCO and its toolbar together — that is what goes full screen. */
    const courseFrame = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        // The browser ends full screen on Esc as well, so re-read the document rather than
        // assuming the button was the only way out.
        const onFullscreenChange = () => {
            const element = document.fullscreenElement;
            setIsFullscreen(element !== null);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    /** Full screen is the learner's choice: everything else keeps the app's chrome. */
    const toggleFullscreen = () => {
        if (document.fullscreenElement) void document.exitFullscreen();
        else void courseFrame.current?.requestFullscreen();
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { course: foundCourse, record: foundRecord, certificate: foundCertificate } =
                await source.load();
            setCourse(foundCourse);
            setRecord(foundRecord);
            setCertificate(foundCertificate);
            setCompleted(foundRecord.status === 'Completed');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to load this course');
        } finally {
            setLoading(false);
        }
        // Opening an onboarding course is what marks it In Progress — a mandatory pre-start
        // course is opened straight from the portal, with no intro page to press Start on.
        // A failure to record that must not stop the hire reaching the material, so the page
        // still renders and the error is only reported.
        if (source.start) {
            try {
                await source.start();
            } catch (err) {
                toast.error(err instanceof Error ? err.message : 'Failed to start this course');
            }
        }
    }, [source]);

    /**
     * Re-reads the record WITHOUT dropping the player. `load` flips the page into its skeleton,
     * which would unmount the iframe and restart the package mid-lesson — so a refresh that
     * follows a package report must not use it.
     */
    const refreshRecord = useCallback(async () => {
        try {
            const { record: current, certificate: currentCertificate } = await source.refresh();
            setRecord(current);
            setCompleted(current.status === 'Completed');
            setCertificate(currentCertificate);
        } catch {
            // A failed refresh must never disturb a course in progress.
        }
    }, [source]);

    useEffect(() => {
        if (allowed && Number.isFinite(courseId)) load();
    }, [allowed, courseId, load]);

    /**
     * A SCORM package is served by the API behind a short-lived token scoped to this learner
     * and this assignment — that is what lets the content report its own progress (§13.1).
     * Minted once per visit, for finished courses too: reopening one is legitimate, and the
     * runtime can never demote a completed assignment (it only reports `track` unless it passes).
     *
     * It waits for `resume`: when there is a bookmark the learner is asked first, because the
     * question cannot be asked later — the package's own prompt is a synchronous `confirm()`
     * (§17.10). Courses with nothing to resume to resolve themselves without asking.
     */
    useEffect(() => {
        if (!allowed || course?.contentType !== 'SCORM') return;
        if (resume === null || scormSession) return;
        let cancelled = false;
        (async () => {
            try {
                const session = await source.session(resume === false);
                if (!cancelled) setScormSession(session);
            } catch (err) {
                if (!cancelled) {
                    setScormError(err instanceof Error ? err.message : 'This package is not available.');
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [allowed, courseId, course?.contentType, resume, scormSession, source]);

    /**
     * Settle the resume question as soon as the record is known: a course with a bookmark and
     * somewhere to go back to shows the dialog (by staying unresolved), anything else resolves
     * itself so the package launches without a pointless prompt.
     */
    useEffect(() => {
        if (resume !== null || !record) return;
        setResume(!record.scormBookmark || completed ? true : null);
    }, [record, completed, resume]);

    /**
     * The shim pings the parent after every commit, so a course can complete while this page
     * is simply sitting there. The message is only a CUE — the state is re-read from the API,
     * so a spoofed message can at worst cause a harmless refresh.
     */
    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            const data = event.data as { source?: string } | null;
            if (!data || data.source !== 'idarah-scorm') return;
            void refreshRecord();
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [refreshRecord]);

    const handleComplete = async () => {
        try {
            setSubmitting(true);
            await source.complete();
            setCompleted(true);
            await refreshRecord();
            toast.success('Course marked as complete');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to complete the course');
        } finally {
            setSubmitting(false);
        }
    };

    if (!allowed) return <AccessRestricted />;
    if (loading) return <LoadingPageSkeleton />;

    const title = course?.title ?? record?.courseTitle ?? 'Course';
    const exitPath = onboarding ? '/my-onboarding?tab=learning' : '/my-learning';

    /**
     * SCORM courses are completed BY the package, never by a button: an always-available "mark
     * complete" let a learner finish a course before commencing it, which defeats the tracking
     * the package exists to do. PDF and PPTX have no runtime, so their button stays.
     */
    const showSelfAttest = course?.contentType !== 'SCORM';

    /** The dialog is up exactly while the learner has not answered the resume question. */
    const needsResumeChoice = Boolean(record?.scormBookmark) && !completed && resume === null;

    const renderBody = () => {
        if (!course) {
            return (
                <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card p-7 text-center">
                    <div className="text-base font-semibold">This course's content isn't available right now</div>
                    <p className="max-w-sm text-sm text-muted-foreground">
                        The course may have been archived or hidden from the library. Your training record stays
                        valid — contact HR if you need the material again.
                    </p>
                </div>
            );
        }

        if (course.contentType === 'PDF') {
            if (course.contentFileUrl) {
                return (
                    <iframe
                        src={course.contentFileUrl}
                        title={course.title}
                        className="w-full h-[70vh] rounded-md border"
                    />
                );
            }
            return (
                <div className="flex min-h-[320px] flex-col gap-3 rounded-lg border border-border bg-card p-7">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">
                        PDF · rendered inline by the browser
                    </div>
                    <div className="text-lg font-semibold">{course.title}</div>
                    <div className="flex flex-col gap-2">
                        {[92, 78, 86, 64, 88, 52].map((width) => (
                            <span
                                key={width}
                                className="block h-2.5 rounded bg-muted"
                                style={{ width: `${width}%` }}
                            />
                        ))}
                    </div>
                    <div className="mt-auto text-xs text-muted-foreground">Page 1 of 6 · scroll to read</div>
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                        No file is attached to this course yet — this is a placeholder. The PDF must be uploaded
                        before it can be read here.
                    </div>
                </div>
            );
        }

        if (course.contentType === 'PPTX') {
            return (
                <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card p-7 text-center">
                    <Presentation size={30} className="text-muted-foreground" />
                    <div className="text-base font-semibold">{course.title}</div>
                    <div className="max-w-sm text-sm leading-relaxed text-muted-foreground">
                        Browsers can't render PPTX. Download the slides, work through them at your own pace, then
                        mark this course as complete.
                    </div>
                    {course.contentFileUrl ? (
                        <Button asChild variant="outline" size="sm">
                            <a href={course.contentFileUrl} target="_blank" rel="noreferrer">
                                <Download size={14} /> Download to view
                            </a>
                        </Button>
                    ) : (
                        <Button variant="outline" size="sm" disabled>
                            <Download size={14} /> No file attached yet
                        </Button>
                    )}
                </div>
            );
        }

        if (course.contentType === 'SCORM') {
            if (scormError) {
                return (
                    <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-destructive/40 bg-destructive/5 p-7 text-center">
                        <div className="text-base font-semibold text-destructive">
                            This package can't be played right now
                        </div>
                        <p className="max-w-sm text-sm text-muted-foreground">{scormError}</p>
                    </div>
                );
            }
            if (!scormSession) {
                return (
                    <div className="flex min-h-[240px] items-center justify-center gap-2 rounded-lg border border-border bg-card p-7 text-sm text-muted-foreground">
                        <Loader2 size={16} className="animate-spin" /> Preparing the course…
                    </div>
                );
            }
            return (
                <div
                    ref={courseFrame}
                    className={cn(
                        'flex flex-col gap-2',
                        // In full screen the wrapper owns the viewport, so the SCO gets all of it.
                        isFullscreen && 'h-screen w-screen bg-background p-3',
                    )}
                >
                    <iframe
                        // Re-keyed per session so a new token never reuses the old document.
                        key={scormSession.token}
                        src={`${API_BASE}${scormSession.frameUrl}`}
                        title={course.title}
                        className={cn(
                            'w-full rounded-md border bg-white',
                            // Fills the viewport rather than a slice of it: a SCORM package is a
                            // whole course, not a document to scroll past.
                            isFullscreen ? 'min-h-0 flex-1' : 'h-[calc(100vh-210px)] min-h-[440px]',
                        )}
                        allow="fullscreen"
                    />
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs leading-relaxed text-muted-foreground">
                            This course records your progress as you work through it — finish it in the
                            player and your result is saved automatically.
                        </p>
                        <Button variant="outline" size="sm" type="button" onClick={toggleFullscreen}>
                            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                            {isFullscreen ? 'Exit full screen' : 'Full screen'}
                        </Button>
                    </div>
                </div>
            );
        }

        // PDF / PPTX / SCORM are the content types; anything else has no body to render.
        return (
            <div className="flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card p-7 text-center">
                <div className="text-base font-semibold">This content type isn't supported yet</div>
                <p className="max-w-sm text-sm text-muted-foreground">
                    Mark the course as complete once you have worked through it.
                </p>
            </div>
        );
    };

    return (
        <div className="p-2 md:p-0">
            <CoursePlayerShell
                title={title}
                categoryName={course?.category?.name ?? record?.category?.name ?? null}
                categoryColour={course?.category?.colour ?? record?.category?.colour ?? null}
                contentTypeLabel={course ? courseContentTypeLabel(course.contentType) : 'Course content'}
                onExit={() => navigate(exitPath)}
                completed={completed}
            >
                {renderBody()}

                {!completed && showSelfAttest && (
                    <Button
                        className="h-10 w-full text-sm font-semibold"
                        onClick={handleComplete}
                        disabled={submitting}
                    >
                        Mark as Complete
                    </Button>
                )}

                {completed && (
                    <div className="flex flex-col items-center gap-2.5 rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-center">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600">
                            <Check size={22} className="text-white" />
                        </div>
                        <div className="text-base font-semibold text-emerald-800">Course complete</div>
                        {onboarding ? (
                            course?.issuesCertificate && (
                                <p className="max-w-sm text-xs text-emerald-700">
                                    Your certificate is issued when HR adds this course to your learner profile.
                                </p>
                            )
                        ) : (
                            certificate && (
                                <div className="flex flex-col items-center gap-2">
                                    <div className="font-mono text-xs text-emerald-700">
                                        {certificate.certificateNumber}
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={() =>
                                            void generateLearningCertificatePdf(
                                                certificate,
                                                currentUser?.name || ''
                                            )
                                        }
                                    >
                                        <Download size={15} /> Download Certificate
                                    </Button>
                                </div>
                            )
                        )}
                        <Button variant="outline" size="sm" onClick={() => navigate(exitPath)}>
                            {onboarding ? 'Back to Onboarding Portal' : 'Back to My Learning'}
                        </Button>
                        {onboarding && (
                            <p className="text-xs text-emerald-700">
                                Your completed training shows on the{' '}
                                <a href="/my-onboarding?tab=learning" className="font-semibold underline">
                                    onboarding portal
                                </a>
                                .
                            </p>
                        )}
                    </div>
                )}
            </CoursePlayerShell>

            {/*
              The resume question lives here, not in the package: the package's own prompt is a
              synchronous `confirm()`, and a real dialog cannot replace it without deadlocking the
              tab (§17.10). The learner's answer travels in the session token, and the shim answers
              the package's own prompt from it.
            */}
            <AlertDialog open={needsResumeChoice}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Continue where you left off?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You were part-way through this course. Pick up from where you stopped, or
                            start it again from the beginning.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setResume(false)}>
                            Start from the beginning
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={() => setResume(true)}>Continue</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
